/**
 * THE MERGE ENGINE — "these two rows are one word".
 *
 * WHY THIS EXISTS
 * ---------------
 * Everything else in the app assumes the segment LIST is right and only its
 * labels and boundaries are in doubt: Verify judges a label, Correct replaces
 * one, the WaveTrimmer moves a cut. None of them can say "these two rows are
 * one word" — and fast connected speech makes that necessary, because
 * "انا اسمي امجد" runs together with no silence to cut on, so the pipeline
 * splits it at a guess and every stage after it then tries to defend the guess.
 *
 * Merging lives here rather than in `db` because it has to re-cut audio.
 * Multi-word labels are valid in this app: `estimateSyllables` already models
 * them, and the export carries whatever text the human wrote.
 *
 * WHAT USED TO BE IN THIS FILE
 * ----------------------------
 * It had accumulated four unrelated engines. They now live where they belong:
 *
 *   moveBoundary / setBoundaries   -> `services/boundaryEdits.ts`
 *   transferEdgeWord / convertToSilence -> `services/cutOps.ts` (same recut
 *                                      engine, same page)
 *   splitSegment                   -> `services/legacy/splitSegment.ts`
 *                                      (superseded design, no entry point)
 *
 * The sibling operation to a merge — "this row should not exist" — is
 * `db.removeSegment`, which is storage-only and so lives in the db layer. Note
 * that it HARD-DELETES, while `cutOps.convertToSilence` is the versioned
 * alternative and has no caller; see ARCHITECTURE.md's follow-up F2.
 *
 * ATOMICITY
 * ---------
 * A merge goes through `reviseSegments`: the survivor's new row, every
 * renumbered row after it and the absorbed row's deletion land in ONE IndexedDB
 * transaction, so a failure can never leave two segments claiming one index or a
 * clip with no segment. `reconcileTask` runs after, because merging away the
 * last unresolved row of a task legitimately completes it.
 */

import {
  getSegment,
  getSegmentsByTask,
  reconcileTask,
  reviseSegments,
  updateSegment,
  type SegmentRevision,
} from '../db';
import { isActiveLeaf, type Segment, type TaskStatus } from '../types';
import { sliceToWav } from './audioSlicer';
import { decodedAudioFor } from './decodedAudio';

/** The shared single-slot decode, with this engine's own failure wording. */
const bufferFor = (taskId: string): Promise<AudioBuffer> =>
  decodedAudioFor(taskId, 'The original audio for this task is missing, so the clip cannot be re-cut.');

/* ------------------------------------------------------------------ */
/* Neighbours                                                          */
/* ------------------------------------------------------------------ */

/** The rows either side of a segment IN ITS TASK, whatever queue it is in. */
export interface Neighbours {
  prev?: Segment;
  next?: Segment;
}

/**
 * A merge target is a task neighbour, not a queue neighbour.
 *
 * The Correct queue holds only the rows marked incorrect, so the segment
 * physically before the one on screen is usually not the card above it. Merging
 * against the card above would splice together two rows with a stranger between
 * them and produce a clip containing audio neither label describes.
 */
export async function getNeighbours(segment: Pick<Segment, 'id' | 'taskId'>): Promise<Neighbours> {
  const siblings = (await getSegmentsByTask(segment.taskId)).filter(isActiveLeaf);
  const at = siblings.findIndex((s) => s.id === segment.id);
  if (at < 0) return {};
  return { prev: siblings[at - 1], next: siblings[at + 1] };
}

/* ------------------------------------------------------------------ */
/* Merge                                                               */
/* ------------------------------------------------------------------ */

export interface MergeOptions {
  /** Replacement label for the merged row. Defaults to the two joined by a space. */
  text?: string;
  /**
   * A human typed/approved {@link MergeOptions.text} for this exact merged span.
   *
   * The ONLY thing that buys confidence 1.0. A merge on its own is a structural
   * claim ("these are one clip"), not a claim about the words: the concatenated
   * label is still two machine guesses with a space in it, and letting it inherit
   * a high confidence would launder the guess into ground truth. Unconfirmed
   * merges keep the WORSE of the two confidences.
   */
  confirmed?: boolean;
}

export interface MergeOutcome {
  merged: Segment;
  /** The absorbed row, now deleted. */
  removedId: string;
  removedLabel: string;
  taskId: string;
  /** How many rows after the merge were renumbered. */
  renumbered: number;
  status?: TaskStatus;
}

/**
 * Fuse a segment with the row before or after it in the same task.
 *
 * The earlier row survives (its id, its verify state and any human correction
 * carry on), takes the union span, and gets a freshly cut clip through the same
 * `sliceToWav` path the first pass uses. The later row is deleted and everything
 * after it renumbered, in the same transaction.
 */
export async function mergeAdjacentSegments(
  segmentId: string,
  direction: 'prev' | 'next',
  options: MergeOptions = {},
): Promise<MergeOutcome> {
  const seg = await getSegment(segmentId);
  if (!seg) throw new Error('That segment is no longer there.');

  const siblings = (await getSegmentsByTask(seg.taskId)).filter(isActiveLeaf);
  const at = siblings.findIndex((s) => s.id === segmentId);
  if (at < 0) throw new Error('That segment is no longer part of its task.');
  const other = direction === 'prev' ? siblings[at - 1] : siblings[at + 1];
  if (!other) {
    throw new Error(
      direction === 'prev' ? 'This is the first segment of the task.' : 'This is the last segment of the task.',
    );
  }

  const [left, right] = seg.index < other.index ? [seg, other] : [other, seg];
  const buffer = await bufferFor(seg.taskId);

  const startSec = Math.min(left.startSec, right.startSec);
  const endSec = Math.max(left.endSec, right.endSec);
  const joined = `${left.heard} ${right.heard}`.replace(/\s+/g, ' ').trim();
  const text = options.text?.trim() || joined;
  const joinedContext = `${left.contextual || left.heard} ${right.contextual || right.heard}`
    .replace(/\s+/g, ' ')
    .trim();

  const merged: Segment = {
    ...left,
    startSec,
    endSec,
    heard: text,
    contextual: options.text?.trim() ? text : joinedContext,
    // Two different kinds fused into one clip is a phrase, and a phrase is a
    // word for this app's purposes; two of a kind keep it.
    kind: left.kind === right.kind ? left.kind : 'word',
    confidence: options.confirmed ? 1 : Math.min(left.confidence, right.confidence),
  };
  // Whatever either row was suspected of, the merged span is a new thing a human
  // asked for. A stale machine suggestion must not ride along on it.
  delete merged.suggestedRemoval;
  if (options.confirmed && options.text?.trim() && merged.verify === 'incorrect') {
    // The human typed the label for this exact span — that IS the correction,
    // so the row stops being an open item in the Correct queue.
    merged.correctedText = text;
  }

  const survivors = siblings.filter((s) => s.id !== right.id);
  const revisions: SegmentRevision[] = [];
  survivors.forEach((s, index) => {
    if (s.id === merged.id) {
      revisions.push({
        segment: { ...merged, index },
        clip: sliceToWav(buffer, startSec, endSec),
      });
      return;
    }
    if (s.index !== index) revisions.push({ segment: { ...s, index } });
  });

  await reviseSegments(revisions, [right.id]);
  const status = await reconcileTask(seg.taskId);
  const written = revisions.find((r) => r.segment.id === merged.id)?.segment ?? merged;
  return {
    merged: written,
    removedId: right.id,
    removedLabel: right.heard,
    taskId: seg.taskId,
    renumbered: revisions.length - 1,
    status,
  };
}

/**
 * "Yes, that is what the merged clip says."
 *
 * The one path that promotes a merged label to confidence 1: a human listened to
 * the combined span and wrote what it says.
 *
 * The text goes to `correctedText`, never to `heard` — `heard` is what the
 * machine claimed and the export's whole value is being able to tell the two
 * apart (`finalTextOf` already prefers the correction). The row is marked
 * `incorrect` first when it was still pending, because "a human supplied the
 * label" is exactly what that pair of fields means everywhere else in the app,
 * and it makes confirming resolve the row in one tap instead of two.
 */
export async function confirmMergedLabel(
  segmentId: string,
  text: string,
): Promise<{ segment?: Segment; status?: TaskStatus }> {
  const trimmed = text.trim();
  if (!trimmed) throw new Error('Type the words you hear before confirming.');
  const seg = await getSegment(segmentId);
  if (!seg) throw new Error('That segment is no longer there.');
  const segment = await updateSegment(segmentId, {
    verify: 'incorrect',
    correctedText: trimmed,
    confidence: 1,
    placedBy: 'human',
  });
  const status = await reconcileTask(seg.taskId);
  return { segment, status };
}
